import Button from "@/components/ui/button";
import type { Brandkit, FormBrand } from "../../types/api.types";

interface StepExpiredProps {
	brandkit: Brandkit;
	brand: FormBrand;
	brandLogo?: string | null;
	isDarkMode?: boolean;
}

export default function StepExpired({
	brandkit,
	brand,
	isDarkMode,
}: StepExpiredProps) {
	const handleReload = () => {
		window.location.reload();
	};
	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<div
			className={`min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden ${isDarkMode ? "bg-gray-950" : "bg-white"}`}
		>
			{/* Logo */}
			<div className="animate-fade-in-up mb-6">
				{brandLogo ? (
					<img
						src={brandLogo}
						alt={brand.name}
						className="h-10 md:h-12 w-auto object-contain"
					/>
				) : (
					<span
						className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-primary"}`}
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						{brand.name}
					</span>
				)}
			</div>

			{/* Icon */}
			<div className="animate-fade-in-up animation-delay-100 mb-6">
				<div
					className={`w-20 h-20 rounded-full flex items-center justify-center ${isDarkMode ? "bg-amber-900/30" : "bg-amber-100"}`}
				>
					<svg
						className={`w-10 h-10 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={1.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
			</div>

			{/* Title */}
			<div className="animate-fade-in-up animation-delay-200 text-center">
				<h1
					className={`text-[32px] md:text-[36px] font-semibold leading-tight tracking-tight ${isDarkMode ? "text-white" : "text-t-primary"}`}
					style={{
						fontFamily: "var(--font-heading)",
						fontWeight: "var(--font-heading-weight)",
					}}
				>
					Session Expired
				</h1>
			</div>

			{/* Description */}
			<div className="animate-fade-in-up animation-delay-300 text-center mt-3 max-w-md">
				<p
					className={`text-base md:text-lg leading-relaxed ${isDarkMode ? "text-gray-300" : "text-secondary"}`}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					It looks like you've been away for a while. Please reload
					the page to start again.
				</p>
			</div>

			{/* Reload Button */}
			<div className="animate-fade-in-up animation-delay-400 mt-8">
				<Button
					type="button"
					variant="default"
					onClick={handleReload}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					Reload Page
				</Button>
			</div>

			{/* Powered by badge */}
			<div className="flex justify-center mt-10">
				{/* <span
					className={`text-center sm:text-left text-sm font-sans ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
				>
					Powered by <span className="block sm:inline"></span> Say
					About Us
				</span> */}

				<a
					href="https://sayabout.us/"
					target="_blank"
					rel="noopener noreferrer"
					className="underline hover:opacity-80"
				>
					<img
						src="/images/badge-sayaboutus-2.svg"
						alt="build social proof with Say About Us"
						className="h-7 w-auto"
					/>
				</a>
			</div>
		</div>
	);
}
